import React from 'react';

import {StreamBankLink} from "../StreamBankLink";
import {ActivityLineDescription} from "./Types/ActivityLineDescription";
import {getActivityAction} from "../../../Utils/Activity/getActivityAction";
import {submissionTransition} from "../../../Utils/Activity/submissionTransition";
import {FileUploadFieldViewer} from "../../../Models/Core/FieldViewers";
import {service as ContactService} from "../../../Services/ContactService";
import {service as RelationshipService} from "../../../Services/RelationshipService";
import {service as PermitTypeService} from "../../../Services/PermitTypeService";
import {service as SurveyTypeService} from "../../../Services/SurveyTypeService";
import {service as SpeciesService} from "../../../Services/SpeciesService";
import {FunderSubject} from "./Subjects/FunderSubject";
import {SurveyLink} from "./Subjects/SurveyLink";
import {SpeciesModalSubject} from "./Subjects/SpeciesModalSubject";
import {partitionSurveyAnswerDelta} from "../../../Utils/surveyAnswerUtils";
import DateUtils from "../../../../../monitoring/src/Utils/DateUtils";

/**
 * Per-type facts suppliers for the feed's description line.
 *
 * This is not a data file — it is the switch `ActivityFeedEventRenderFactory` already had, reshaped
 * to return facts instead of a finished component. Metadata lives here beside the supplier that
 * consumes it so a new or changed type is one code change rather than code plus a migration across
 * three environments, and so coverage can be asserted in CI against `ActivityTypeCodes` (which
 * per-environment database state cannot be).
 *
 * A supplier receives `{data, event}` — the parsed `Activity.data` and the ContextNodeActivity row —
 * and returns the facts `buildActivityLine` needs. `subject`, `suffix` and `trailer` may each be a
 * React node, which is what lets links, modals and viewers survive inside a generated sentence.
 * Return null to render nothing.
 *
 * An entry may also carry:
 *
 * - `services`: reference services to prime before the supplier runs, so it can read a name out of
 *   the cache synchronously and decide, on the facts, whether the line is renderable yet. Priming
 *   is deduplicated inside the service, so declaring one costs a single request for the whole feed.
 * - `isSystem`: never attribute an actor, however the row is stored.
 * - `renderable`: given the same `{data, event}`, whether this row belongs in the feed at all.
 *   Returning false drops the whole entry — title, timestamp and pane — rather than leaving a row
 *   with an empty sentence. It is for a row that records no change worth narrating, which is a
 *   different thing from a supplier returning null because a fact has not loaded yet.
 *
 * Wording is fixed by `documentation/Specifications/Activity Description Copy Contract.md`; do not
 * adjust it here without changing that, because the daily-summary email implements the same table
 * in `server/app/lib/App/Utils/Activity/ActivityLineRegistry.php` and the two drifting apart is the
 * failure this design exists to prevent.
 *
 * Every `ActivityTypeCode` has an entry here — that is the whole of the feed's description layer now,
 * and `tests/preflight/check-activity-lines.js` fails if a new code arrives without one, because the
 * symptom otherwise is a blank feed row rather than an error.
 */

/**
 * Whether a stored fact counts as absent. One rule for the whole registry, mirrored by the email's
 * `ActivityLineRegistry::IsBlank`, because a fact that degrades in one renderer and prints in the
 * other is the drift the shared contract exists to prevent. Blank is not merely missing: a key
 * present and empty is as common as an absent one, and `??` alone would pass it through and leave a
 * sentence with no subject or a dangling preposition.
 *
 * The STRING "0" is deliberately present — a field labelled "0" keeps its label — which is also the
 * one case PHP's own falsiness disagrees on, and the reason both sides name the rule rather than
 * relying on their language's truthiness.
 */
const isBlank = (value) =>
    value === null || value === undefined || value === '' || value === 0 || value === false;

/**
 * The changed field's label. Never re-cased — it is a stored human title ("Phone Number") and both
 * renderers print it as recorded — so the two forms differ only in their fallback: sentence case
 * where the field leads the sentence, lower case where it sits inside a phrase.
 */
const fieldSubject = (data) => isBlank(data?.fieldName) ? 'Details' : data.fieldName;
const fieldLabel = (data) => isBlank(data?.fieldName) ? 'details' : data.fieldName;

/**
 * The stored file name, recorded on the activity precisely so the line survives deletion. Both keys
 * occur — deletions record `name`, the rest record `title` — and the email's twin reads them in this
 * same order.
 */
const fileName = (data) => {
    if (!isBlank(data?.name)) {
        return data.name;
    }

    return isBlank(data?.title) ? 'a file' : data.title;
};

/**
 * Association diffs record their own action — only `added`, `modified` and `removed` occur, verified
 * across every row of all seven types that emit them — but the contract's wording for the middle
 * case is not the stored word. Most types call it "updated"; funders and file context assignments
 * call it "changed", because there a modification repoints the association to a *different* funder
 * or node rather than editing the one that was there.
 *
 * `modified` stays the fallback for a row that records no action, as it has been throughout; it maps
 * to the same wording as a recorded modification. An unrecognised action renders verbatim rather
 * than being forced into one of the three, so a future action word shows up as itself.
 */
const associationVerb = (data, modifiedVerb = 'updated') => {
    const action = data?.action ?? 'modified';

    return action === 'modified' ? modifiedVerb : action;
};

/** Fulcrum sends create/created and update/updated interchangeably; anything else is a plain sync. */
const syncVerb = (action) => {
    switch (action) {
        case 'create':
        case 'created':
            return 'added';
        case 'update':
        case 'updated':
            return 'updated';
        default:
            return 'synced';
    }
};

const contextLink = (event, text) =>
    <StreamBankLink Context={event?.ContextNodeDetails?.link}>{text}</StreamBankLink>;

/**
 * A conservation action's own name. The nested key is the newer shape and the top-level one is what
 * deletions record — both name the conservation action, differing only in the naming convention in
 * force when the row was written. Reading the nested key alone left every delete_conservation_action
 * row rendering "Conservation action was removed"; none of the 14 stored rows carries it.
 */
const conservationActionName = (data) => data?.contextNode?.title ?? data?.title ?? '';

/** Dates in a description are always the long readable form, never the storage form. */
const activityDate = (date) => DateUtils.FormatDate(date, "MMM d, yyyy") || null;

/**
 * "{SurveyType} survey" as a link, for the types that record a surveyTypeId rather than a surveyId.
 * The noun sits inside the link so an uncached type degrades to the bare word and the sentence still
 * reads — the component this replaces kept it outside and rendered "the survey survey".
 */
const surveyTypeLink = (event, surveyType) =>
    <StreamBankLink Context={event?.ContextNodeDetails?.link} Tool={surveyType?.code}>
        {isBlank(surveyType?.name) ? 'survey' : `${surveyType.name} survey`}
    </StreamBankLink>;

/**
 * How many answers a survey save actually touched, by the same partition the answers pane displays,
 * so the count in the sentence and the rows in the pane can never disagree. A cleared answer counts
 * once, as the removal that carries its prior value.
 */
const changedSurveyAnswerCount = (data) => {
    const {changed, removed} = partitionSurveyAnswerDelta(data?.oldData, data?.newData);

    return changed.length + removed.length;
};

/**
 * Free text quoted inside a sentence, as an aside the grammar appends before the terminal period.
 * A trailing full stop is dropped because the sentence supplies its own: none of the 96 stored notes
 * ends in one, but they are all test strings, and a real note written as a sentence would render
 * `…: "please fix the acreage.".`
 */
const quotedNote = (notes) => {
    const trimmed = notes?.trim().replace(/\.$/, '');

    return trimmed ? `: "${trimmed}"` : null;
};

/**
 * "{common name} ({latin name})". Species lead their own sentence, so the degraded label needs
 * sentence case — SpeciesService's own fallback is phrased for mid-sentence use. One stored row needs
 * it: the single create_species written before the payload settled on `newData`.
 */
const speciesLabel = (species) => species ? SpeciesService.GetFullTitle(species) : 'A species';

export const ActivityLineRegistry = {
    create_program: {
        facts: ({data, event}) => ({
            subject: <>Program {contextLink(event, data?.title ?? '')}</>,
            verb: 'created',
        }),
    },
    delete_program: {
        facts: ({data}) => ({
            subject: `Program ${data?.title ?? ''}`.trim(),
            verb: 'removed',
        }),
    },
    update_program_details: {
        facts: ({data}) => ({
            subject: fieldSubject(data),
            verb: getActivityAction(data),
            suffix: 'on the program',
        }),
    },
    update_program_projected_uplift: {
        facts: ({data}) => ({
            subject: 'A program environmental benefit',
            verb: associationVerb(data),
        }),
    },

    create_project: {
        facts: ({data, event}) => ({
            subject: <>Project {contextLink(event, data?.title ?? '')}</>,
            verb: 'created',
        }),
    },
    delete_project: {
        facts: ({data}) => ({
            subject: `Project ${data?.title ?? ''}`.trim(),
            verb: 'removed',
        }),
    },
    update_project_details: {
        facts: ({data}) => ({
            subject: fieldSubject(data),
            verb: getActivityAction(data),
            suffix: 'on the project',
        }),
    },
    update_project_aliases: {
        facts: ({data}) => ({
            subject: `The nickname "${data?.value ?? ''}"`,
            verb: associationVerb(data),
        }),
    },
    update_project_taxlots: {
        facts: ({data}) => ({
            subject: `Tax lot "${data?.value ?? ''}"`,
            verb: associationVerb(data),
        }),
    },
    // The relationship — Landowner, Signatory — is a role rather than an identity, so it leads the
    // sentence and reaches the email too; the contact's own name does not. Withheld until the
    // relationship cache primes, matching what the component this replaces did: a cache miss left
    // the row blank rather than rendering a half-built sentence, and the alternative here would be
    // to name a field without saying whose.
    update_project_stakeholders: {
        services: [RelationshipService],
        facts: ({data}) => {
            const relationshipName = RelationshipService.CachedTitleById(data?.relationshipId);
            if (!relationshipName) {
                return null;
            }

            const {stakeholderName = '', stakeholderId = '', action} = data ?? {};

            return {
                subject: <>The {relationshipName} <StreamBankLink
                    // A removed stakeholder is no longer in the current context, so their link has
                    // to resolve from the root instead.
                    Context={action === 'removed' ? {} : null}
                    Tool={"contacts/" + stakeholderId}
                >{stakeholderName}</StreamBankLink></>,
                verb: associationVerb(data),
            };
        },
    },
    // Only the association is named, never the funder's own name: `value` is a funderId that the
    // subject resolves for the feed, and the email's line stays generic.
    update_project_funders: {
        facts: ({data}) => ({
            subject: <FunderSubject FunderId={data?.value} />,
            verb: associationVerb(data, 'changed'),
        }),
    },
    // Feed-only contact name, so this is a divergent type: the feed names the person whose details
    // changed, the email says only which relationship, because a contact's name is a third-party
    // identity that the email's group header does not already disclose. Matches the shape of
    // update_project_stakeholders above, which the same reasoning already governs.
    //
    // Degrades to the email's wording when no name is recorded, which is most of the time — only 7 of
    // 164 stored rows carry a name at all. The before and after values are not in the sentence
    // because every row of this type carries a viewer, and ActivityFeedItem renders the delta pane
    // beside it.
    update_contact_details: {
        services: [RelationshipService],
        facts: ({data}) => {
            const relationshipName = RelationshipService.CachedTitleById(data?.relationshipId);
            if (!relationshipName) {
                return null;
            }

            // The service's own default is the placeholder "(No name)"; an empty one lets the line
            // fall back to naming the relationship alone instead of announcing the absence.
            const contactName = ContactService.GetTitle(data, '').trim();
            const owner = contactName ? `${relationshipName} ${contactName}` : relationshipName;

            return {
                subject: `The ${owner}'s ${fieldLabel(data)}`,
                verb: getActivityAction(data),
            };
        },
    },

    create_conservation_action: {
        facts: ({data, event}) => ({
            subject: <>Conservation action {contextLink(event, conservationActionName(data))}</>,
            verb: 'added',
        }),
    },
    delete_conservation_action: {
        facts: ({data}) => ({
            subject: `Conservation action ${conservationActionName(data)}`.trim(),
            verb: 'removed',
        }),
    },
    update_conservation_action_details: {
        facts: ({data}) => ({
            subject: fieldSubject(data),
            verb: getActivityAction(data),
            suffix: 'on the conservation action',
        }),
    },
    update_conservation_action_modeled_uplift: {
        facts: ({data}) => ({
            subject: 'A modeled uplift',
            verb: associationVerb(data),
        }),
    },

    // The file family keeps FileUploadFieldViewer in the line, which is what carries the file's
    // identity and its link. Putting it in the subject works because a subject is a node and the
    // viewer runs its own hooks where it renders, not where the facts are built. file_delete is the
    // exception: the file is gone by then, so the stored name is rendered plainly — that name is
    // recorded precisely so the line stays readable after deletion. The before/after tag delta on
    // file_tags_update needs nothing here; ActivityFeedItem already renders it as the secondary
    // description whenever a row carries oldValue/newValue/viewer.
    file_create: {
        facts: ({data}) => ({
            subject: <>The file <FileUploadFieldViewer Value={data?.fileUploadId} /></>,
            verb: 'uploaded',
        }),
    },
    file_update: {
        facts: ({data}) => ({
            subject: <>The {fieldLabel(data)} of <FileUploadFieldViewer Value={data?.fileUploadId} /></>,
            verb: getActivityAction(data),
        }),
    },
    file_delete: {
        facts: ({data}) => ({
            subject: <>The file <strong>{fileName(data)}</strong></>,
            verb: 'deleted',
        }),
    },
    file_download: {
        facts: ({data}) => ({
            subject: <>The file <FileUploadFieldViewer Value={data?.fileUploadId} /></>,
            verb: 'downloaded',
        }),
    },
    file_tags_update: {
        facts: ({data}) => ({
            subject: <>Tags on <FileUploadFieldViewer Value={data?.fileUploadId} /></>,
            verb: 'changed',
            plural: true,
        }),
    },
    // Declared in ActivityTypeCodes but emitted nowhere, so its data shape is unobservable until
    // something first fires it. Implemented defensively to retire the bare default; whichever
    // ticket adds an emitter owns confirming the wording against real rows.
    update_file_context_nodes: {
        facts: ({data}) => ({
            subject: <>Context assignments for file <FileUploadFieldViewer Value={data?.fileUploadId} /></>,
            verb: associationVerb(data, 'changed'),
            plural: true,
        }),
    },

    // Counts the answers the pane beside it will list, by the same partition the pane uses, so the
    // sentence and the pane cannot disagree. The answers themselves stay in that right-hand pane —
    // it is wired from the factory's secondary-description switch, not from here, because it is a
    // column of its own rather than a continuation of the sentence.
    //
    // A save that touched no answers is not reported at all. 168 stored rows are like this, and they
    // are one of two things: a no-op save, which the feed should not narrate, or a pre-delta record
    // whose changes were never captured, which the feed cannot narrate. Either way the honest line is
    // no line — the pane for these says "No changes." An empty delta is the only way the count can be
    // zero (a cleared answer still counts, as a removal), so this predicate and the count agree.
    survey_transaction_create: {
        renderable: ({data}) => changedSurveyAnswerCount(data) > 0,
        facts: ({data, event}) => {
            const count = changedSurveyAnswerCount(data);

            return {
                subject: `${count} survey ${count === 1 ? 'answer' : 'answers'}`,
                verb: 'updated',
                plural: count !== 1,
                suffix: <>on the <SurveyLink
                    SurveyId={data?.surveyId}
                    ContextNodeId={data?.contextNodeId}
                    Event={event}
                /></>,
            };
        },
    },
    // Feed-only reviewer notes, which is why they are an aside rather than part of the verb: they are
    // free text that can carry candid assessment, so the email renders the transition alone.
    //
    // The two lines that shipped in the active voice — "{user} submitted the survey for review",
    // "{user} accepted the survey" — are passive here like the other three, which is the whole point
    // of the type joining the grammar; the survey was already the subject of the other three.
    survey_submission_status_change: {
        services: [SurveyTypeService],
        facts: ({data, event}) => {
            const {surveyTypeId, notes} = data ?? {};

            // Which transition the row records, and whether its note belongs to that transition, are
            // read in one shared place so the feed and the email cannot disagree about either — see
            // submissionTransition, which the wording gate runs against the email's PHP twin.
            const surveyLink = surveyTypeLink(event, SurveyTypeService.CachedValueById(surveyTypeId)?.result);
            const {verb, keepsNote} = submissionTransition(data);
            const aside = keepsNote ? quotedNote(notes) : null;

            // A step back to a pre-submit status with no recorded action: either a return or a
            // withdrawal, and nothing stored says which. Naming the change rather than the
            // transition is the neutral reading, and the only one that is true of both.
            if (!verb) {
                return {
                    subject: 'Changes',
                    verb: 'requested',
                    plural: true,
                    suffix: <>on the {surveyLink}</>,
                    aside,
                };
            }

            return {
                subject: <>The {surveyLink}</>,
                verb,
                aside,
            };
        },
    },

    // System-originated, so never actor-attributed however the row is stored: 100% of these rows
    // have a null userId, and the old renderer's "StreamBank synced…" was the invented actor this
    // work exists to remove. Both links the shipped component carried are kept — the sample event
    // and the originating Fulcrum record — which is why the subject and suffix are nodes here. The
    // email renders the same sentence without them.
    sync_sample_event: {
        isSystem: true,
        facts: ({data}) => {
            const {appName, action, link = null, sampleEventLink = null} = data ?? {};

            const subject = sampleEventLink
                ? <StreamBankLink Context={sampleEventLink} Tool="summary">A sample event</StreamBankLink>
                : 'A sample event';
            const record = link
                ? <a href={link} target="_blank" rel="noreferrer">record</a>
                : 'record';

            return {
                subject,
                verb: syncVerb(action),
                suffix: <>from a {record} in the {appName} Fulcrum App</>,
            };
        },
    },

    // These three name no permit type, number or organisation because no row carries them — the
    // only facts stored are the conservation action's name and a permitTypeId. The components these
    // replace read the absent keys and rendered "a undefined from undefined permit". Both emitters
    // are commented out in PermitService, so nothing has produced a row since 2020; whichever ticket
    // restores them owns enriching these lines. `expiring_permit` is unrelated and still live.
    create_permit: {
        facts: ({data}) => ({
            subject: 'A permit',
            verb: 'added',
            suffix: isBlank(data?.title) ? null : `on ${data.title}`,
        }),
    },
    update_permit: {
        facts: ({data}) => ({
            subject: 'A permit',
            verb: 'updated',
            suffix: isBlank(data?.title) ? null : `on ${data.title}`,
        }),
    },
    delete_permit: {
        facts: ({data}) => ({
            subject: 'A permit',
            verb: 'removed',
            suffix: isBlank(data?.title) ? null : `from ${data.title}`,
        }),
    },

    // The species four say what a change to a shared reference list means for programs that use it,
    // which is the second sentence — the trailer, the only slot that lands after the terminal period.
    // Their subject opens the point-in-time species snapshot the activity recorded, which is what
    // needed a node: a facts supplier cannot hold a modal's open state.
    //
    // They gain the grammar's "was", which the components they replace rendered without. The framing
    // "a shared reference list" is the copy that shipped with the species review flow and stands
    // verbatim — it is what tells a reader that the change reaches every program using that list.
    // The copula is this work's; the framing survived the rewrite where the telegraphic voice did not.
    create_species: {
        facts: ({data}) => ({
            subject: <SpeciesModalSubject
                Species={data?.newData}
                Label={speciesLabel(data?.newData)}
            />,
            verb: 'added',
            suffix: 'to a shared reference list',
            trailer: 'Programs that use this reference list will see the new species.',
        }),
    },
    update_species: {
        facts: ({data}) => {
            const label = fieldLabel(data);

            return {
                subject: <SpeciesModalSubject Species={data?.newData} Label={`Species ${label}`} />,
                verb: 'updated',
                suffix: 'in a shared reference list',
                trailer: `Programs that use this reference list will see the updated ${label}.`,
            };
        },
    },
    // "reviewed, but not added" is one verb phrase rather than a verb and a suffix, because the
    // negation has to sit inside it — the list the species was not added to is what follows.
    deny_species: {
        facts: ({data}) => ({
            subject: <SpeciesModalSubject
                Species={data?.newData}
                Label={speciesLabel(data?.newData)}
            />,
            verb: 'reviewed, but not added',
            suffix: 'to a shared reference list',
            trailer: 'Programs that use this reference list are not affected.',
        }),
    },
    // Reads oldData first: by the time a species is removed, newData is the row as it no longer is,
    // and the name worth showing is the one it had. No emitter exists — the ActivityType has been
    // declared since 233.sql and has never fired — so this covers legacy rows and any future one.
    delete_species: {
        facts: ({data}) => {
            const species = data?.oldData ?? data?.newData;

            return {
                subject: <SpeciesModalSubject Species={species} Label={speciesLabel(species)} />,
                verb: 'removed',
                suffix: 'from a shared reference list',
                trailer: 'Programs that use this reference list will no longer see this species.',
            };
        },
    },

    // The one permit type with a live emitter, and the only line in the whole set that is not a
    // report of something that happened — a renewal deadline is an instruction, which is the entire
    // reason the grammar has a copula override. Written by the expiry-warning command, so there is
    // never an actor.
    //
    // Its facts are the richest in the registry and every one of them can be absent, so each clause
    // is conditional and the sentence contracts to whatever is known rather than printing "undefined"
    // — which is the failure the permit CRUD lines above were shipping. Two go beyond the component
    // this replaces: the permit type, which its contract line always named but the component omitted,
    // and the expiry sentence, which the component built and then never rendered.
    //
    // The type name is the noun rather than an adjective before a literal "permit", which is both
    // how PermitService titles a permit and the only phrasing the data supports: 21 of the 31 types
    // already end in "Permit", so "the {type} permit" stutters, and 10 of them are not permits at all
    // — a Concurrence Letter, a Water Right, a Section 7 Consultation. The generic word is the
    // fallback for when the type is not resolvable, which is also the wording this line shipped with.
    expiring_permit: {
        isSystem: true,
        services: [PermitTypeService],
        facts: ({data}) => {
            const {permit, permitOrg, conservationActions} = data ?? {};
            const renewalDate = activityDate(permit?.renewalDate);
            const expirationDate = activityDate(permit?.expirationDate);

            return {
                subject: [
                    'The',
                    PermitTypeService.CachedTitleById(permit?.permitTypeId) ?? 'permit',
                    permit?.permitNumber,
                    // Blank, not merely absent: one of the three stored rows carries
                    // conservationActions as an empty string, which would read as a dangling "for ".
                    isBlank(permitOrg) ? null : `from ${permitOrg}`,
                    isBlank(conservationActions) ? null : `for ${conservationActions}`,
                ].filter(Boolean).join(' '),
                copula: 'must be',
                verb: 'renewed',
                suffix: renewalDate ? `by ${renewalDate}` : null,
                trailer: expirationDate ? `It will expire on ${expirationDate}.` : null,
            };
        },
    },
};

/**
 * Components are cached per activity type because the factory is consulted on every render, and a
 * fresh component identity each time would remount the row and lose any state beneath it.
 */
const boundDescriptions = {};

/**
 * The description component for an activity type, or null if the row should not be rendered.
 *
 * `context` is the `{data, event}` an entry's `renderable` predicate needs. It is optional so a
 * caller that only wants to know whether a type is described at all can omit it; when a type declares
 * no predicate it makes no difference either way.
 */
export const getActivityLineDescription = (code, context = null) => {
    const entry = ActivityLineRegistry[code];
    if (!entry) {
        return null;
    }

    if (context && entry.renderable && !entry.renderable(context)) {
        return null;
    }

    if (!boundDescriptions[code]) {
        const BoundActivityLineDescription = props => <ActivityLineDescription {...props} Entry={entry} />;
        BoundActivityLineDescription.displayName = `ActivityLineDescription(${code})`;
        boundDescriptions[code] = BoundActivityLineDescription;
    }

    return boundDescriptions[code];
};
